import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { collection, getDocs, doc, Timestamp, updateDoc, startAfter, query, limit, orderBy, where, OrderByDirection, getDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebase-config';
import { Pagination } from '../types/common';
import { CustomProduct, CustomProductGeneration, CustomProductInput } from '../types/customProducts';

export type { CustomProduct, CustomProductGeneration, CustomProductInput, Pagination };

const customProductKeywordFields: Array<keyof CustomProductInput> = [
  'globalMustHaveKeywords',
  'titleHighPriorityKeywords',
  'titleOptionalKeywords',
  'bulletPointHighPriorityKeywords',
  'bulletPointOptionalKeywords',
  'descriptionHighPriorityKeywords',
  'descriptionOptionalKeywords',
  'negativeKeywords',
];

const getCustomProductKeywordCount = (input?: CustomProductInput): number => {
  if (!input) return 0;
  return customProductKeywordFields.reduce((count, field) => {
    const value = input[field];
    return count + (Array.isArray(value) ? value.length : 0);
  }, 0);
};

const getKeywordMetadataUpdate = (input?: CustomProductInput) => {
  const keywordCount = getCustomProductKeywordCount(input);
  return {
    hasKeywords: keywordCount > 0,
    keywordCount,
  };
};


// TODO: this is only a placeholder for now, we need to update all the interafces and we still need to think about if we really want to call
// them products or  products

interface CustomProductState {
  customProducts: CustomProduct[];
  selectedCustomProduct: CustomProduct | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  // pagination
  pagination: Pagination;
  activeFilters: Record<string, any>;
  activeSorter: { field: string; order: OrderByDirection } | null;
}

const initialState: CustomProductState = {
  customProducts: [],
  selectedCustomProduct: null,
  status: 'idle',
  error: null,
  pagination: {
    page: 1,
    pageSize: 20,
    lastVisibleDoc: null,
    hasMore: true
  },
  activeFilters: {},
  activeSorter: null,
};

export const deleteCustomProduct = createAsyncThunk(
  'customProducts/deleteCustomProduct',
  async ({ id, companyId }: { id: string; companyId: string }, { rejectWithValue }) => {
    try {
      // Delete the document from Firestore
      console.log("Deleting custom product with ID:", id, "for company ID:", companyId);
      await deleteDoc(doc(db, 'companies', companyId, 'customProducts', id));
      return id; // Return the ID of the deleted custom product
    } catch (error) {
      console.error("Error deleting custom product:", error);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      } else {
        return rejectWithValue('An unknown error occurred');
      }
    }
  }
);

// export const updateCustomProductGeneration = createAsyncThunk(
//   'customProducts/updateCustomProductGeneration',
//   async (
//     args: { id: string; companyId: string; generation: Partial<ProuctGeneration> },
//     { rejectWithValue }
//   ) => {
//     try {
//       const { id, companyId, generation } = args;
//       const timestamp = Timestamp.now();

//       // Convert to dot notation for Firestore
//       const generationUpdates: Record<string, any> = {};
//       for (const [key, value] of Object.entries(generation)) {
//         generationUpdates[`generation.${key}`] = value;
//       }

//       await updateDoc(doc(db, 'Companies', companyId, 'CustomProducts', id), {
//         ...generationUpdates,
//         updatedAt: timestamp,
//       });

//       return { id, ...generationUpdates, updatedAt: timestamp };
//     } catch (error) {
//       if (error instanceof Error) {
//         return rejectWithValue(error.message);
//       } else {
//         return rejectWithValue('An unknown error occurred');
//       }
//     }
//   }
// );


export const updateCustomProduct = createAsyncThunk(
  'customProducts/updateCustomProduct',
  async (updatedCompany: Partial<CustomProduct> & { id: string, companyId: string }, { rejectWithValue }) => {
    try {
      const { id, companyId, ...updates } = updatedCompany;
      const timestamp = Timestamp.now();

      // Update the document in Firestore
      const keywordMetadata = updates.input
        ? getKeywordMetadataUpdate(updates.input)
        : {};

      await updateDoc(doc(db, 'companies', companyId, 'customProducts', id), {
        ...updates,
        ...keywordMetadata,
        updatedAt: timestamp,  // Automatically update the updatedAt timestamp
      });

      return { id, ...updates, ...keywordMetadata, updatedAt: timestamp };
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      } else {
        return rejectWithValue('An unknown error occurred');
      }
    }
  }
);


export const refetchCustomProduct = createAsyncThunk(
  'customProducts/refetchCustomProduct',
  async ({ companyId, customProductId }: { companyId: string; customProductId: string }, { rejectWithValue }) => {
    try {
      const docRef = doc(db, 'companies', companyId, 'customProducts', customProductId);
      const docSnapshot = await getDoc(docRef);

      if (!docSnapshot.exists()) {
        throw new Error(`CustomProduct with ID ${customProductId} not found`);
      }

      return {
        id: docSnapshot.id,
        ...docSnapshot.data(),
      } as CustomProduct;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      } else {
        return rejectWithValue('An unknown error occurred');
      }
    }
  }
);

export const fetchCustomProducts = createAsyncThunk(
  'customProducts/fetchCompanyCustomProducts',
  async (
    {
      companyId,
      pageSize,
      lastVisibleDoc,
      activeSorter,
      activeFilters,
      refetch
    }: {
      companyId: string;
      pageSize: number;
      lastVisibleDoc: any | null;
      activeSorter?: { field: string; order: OrderByDirection } | null
      activeFilters: Record<string, any>
      refetch: boolean
    },
    { rejectWithValue }
  ) => {
    try {
      const constraints = [];
      if (activeSorter) {
        constraints.push(orderBy(activeSorter.field, activeSorter.order));
      }

      // Apply filters dynamically
      Object.entries(activeFilters).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          if (key === 'descriptionHtml' && value.length === 1) {
            if (value[0]) {
              constraints.push(where('descriptionHtml', '>', ''));
            } else {
              constraints.push(where('descriptionHtml', '==', null));
            }
          } else if (key === 'brand') {
            console.log("Filter by brand", value)
            constraints.push(where("input.brand", 'in', value));
          } else if (key === 'marketplace') {
            constraints.push(where("input.marketplace", '==', value[0]));
          } else if (key === 'hasKeywords') {
            constraints.push(where('hasKeywords', '==', value[0]));
          } else if (key === 'title') {
            constraints.push(
              where('title', '>=', value[0]),
              where('title', '<=', value[0] + '\uf8ff')
            );
          } else {
            constraints.push(where(key, '==', value[0]));
          }
        }
      });

      // Handle pagination based on direction
      if (lastVisibleDoc) {
        constraints.push(startAfter(lastVisibleDoc));
      }
      constraints.push(limit(pageSize));

      const queryRef = query(collection(db, 'companies', companyId, 'customProducts'), ...constraints);

      const querySnapshot = await getDocs(queryRef);
      const docs = querySnapshot.docs;
      console.log("length", docs.length)
      console.log("docs", docs)

      const lastDoc = docs.length > 0 ? docs[querySnapshot.docs.length - 1] : null;

      return {
        customproducts: querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as CustomProduct[],
        lastVisibleDoc: lastDoc,
        hasMore: querySnapshot.docs.length === pageSize,
        activeFilters: activeFilters,
        activeSorter: activeSorter ? activeSorter : null,
        reftech: refetch
      };
    } catch (error: any) {
      console.error("Error in fetchCustomProducts:", error);
      return rejectWithValue(error.message || 'An unknown error occurred');
    }
  }
);


const customProductsSlice = createSlice({
  name: 'customProducts',
  initialState,
  reducers: {
    setCustomProductPagination: (state, action: PayloadAction<Pagination>) => {
      state.pagination = action.payload;
    },
    setCustomProducts: (state, action: PayloadAction<CustomProduct[]>) => {
      state.customProducts = action.payload;
    },
    resetCustomProducts: (state) => {
      state.customProducts = [];
      state.pagination = { page: 1, pageSize: 20, lastVisibleDoc: null, hasMore: true };
      state.status = 'idle';
      state.error = null;
      state.activeFilters = {};
      state.activeSorter = null
      state.selectedCustomProduct = null
    },
    setSelectedCustomProduct: (state, action: PayloadAction<CustomProduct | null>) => {
      state.selectedCustomProduct = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomProducts.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCustomProducts.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // If the filters are different or the active sorter to the current selected filters is should not be appended but rewritten
        const filtersChanged = JSON.stringify(state.activeFilters) !== JSON.stringify(action.payload.activeFilters)
        const sorterChanged = JSON.stringify(state.activeSorter) !== JSON.stringify(action.payload.activeSorter)
        if (filtersChanged || sorterChanged || action.payload.reftech) {
          state.customProducts = action.payload.customproducts;
        } else {
          state.customProducts = [...state.customProducts, ...action.payload.customproducts];
        }
        // state.CustomProducts = [...state.CustomProducts, ...action.payload.customproducts];
        state.pagination.lastVisibleDoc = action.payload.hasMore ? action.payload.lastVisibleDoc : state.pagination.lastVisibleDoc;
        state.pagination.hasMore = action.payload.hasMore
        state.activeFilters = action.payload.activeFilters
        state.activeSorter = action.payload.activeSorter
      })
      .addCase(fetchCustomProducts.rejected, (state, action) => {
        state.status = 'failed';
        state.error = `Failed to fetch  customproducts: ${action.error.message}`;
      })
      .addCase(updateCustomProduct.pending, (state, action) => {
        const { id, ...updates } = action.meta.arg;
        const index = state.customProducts.findIndex((customProduct) => customProduct.id === id);

        // Optimistically update the customproduct in the state
        if (index !== -1) {
          state.customProducts[index] = {
            ...state.customProducts[index],
            ...updates,
          };
        }
      })
      .addCase(updateCustomProduct.fulfilled, (state, action) => {
        const { id } = action.payload;
        const index = state.customProducts.findIndex((customProduct) => customProduct.id === id);

        // Replace the customproduct with the updated customproduct from the server
        if (index !== -1) {
          state.customProducts[index] = {
            ...state.customProducts[index],
            ...action.payload,
          };
        }
      })
      .addCase(updateCustomProduct.rejected, (state, action) => {
        const { id, ...updates } = action.meta.arg;
        const index = state.customProducts.findIndex((customProduct) => customProduct.id === id);

        // Revert the optimistic update if the backend operation fails
        if (index !== -1) {
          state.customProducts[index] = {
            ...state.customProducts[index],
            ...updates,
            // status: 'failed to update', // Add a flag to indicate failure
          };
        }

        state.error = `Failed to update  customproduct: ${action.error.message}`;
      }).addCase(deleteCustomProduct.fulfilled, (state, action) => {
        const id = action.payload; // The ID of the deleted custom product  
        state.customProducts = state.customProducts.filter((customProduct) => customProduct.id !== id);
        state.status = 'succeeded';
      })
      // .addCase(updateCustomProductGeneration.pending, (state, action) => {
      //   const { id, ...updates } = action.meta.arg;
      //   const index = state.CustomProducts.findIndex((customproduct) => customproduct.id === id);

      //   // Optimistically update the customproduct in the state
      //   if (index !== -1) {
      //     state.CustomProducts[index] = {
      //       ...state.CustomProducts[index],
      //       generation: {
      //         ...state.CustomProducts[index].generation,
      //         ...updates.generation
      //       },
      //     };
      //   }
      // })
      // .addCase(updateCustomProductGeneration.fulfilled, (state, action) => {
      //   const { id } = action.payload;
      //   const index = state.CustomProducts.findIndex((customproduct) => customproduct.id === id);

      //   // Replace the customproduct with the updated customproduct from the server
      //   if (index !== -1) {
      //     state.CustomProducts[index] = {
      //       ...state.CustomProducts[index],
      //       ...action.payload,
      //     };
      //   }
      // })
      // .addCase(updateCustomProductGeneration.rejected, (state, action) => {
      //   const { id, ...updates } = action.meta.arg;
      //   const index = state.CustomProducts.findIndex((customproduct) => customproduct.id === id);

      //   // Revert the optimistic update if the backend operation fails
      //   if (index !== -1) {
      //     state.CustomProducts[index] = {
      //       ...state.CustomProducts[index],
      //       generation: {
      //         ...state.CustomProducts[index].generation,
      //         ...updates.generation
      //       },
      //       // status: 'failed to update', // Add a flag to indicate failure
      //     };
      //   }

      //   state.error = `Failed to update  customproduct: ${action.error.message}`;
      // })
      .addCase(refetchCustomProduct.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(refetchCustomProduct.fulfilled, (state, action) => {
        const updatedCustomProduct = action.payload;
        const index = state.customProducts.findIndex((customProduct) => customProduct.id === updatedCustomProduct.id);

        // Update the customproduct in the state
        if (index !== -1) {
          state.customProducts[index] = updatedCustomProduct;
        }

        state.status = 'succeeded';
      })
      .addCase(refetchCustomProduct.rejected, (state, action) => {
        state.status = 'failed';
        state.error = `Failed to refetch customProduct: ${action.error.message}`;
      });

  },
});

export const { setCustomProducts, setSelectedCustomProduct, setCustomProductPagination, resetCustomProducts } = customProductsSlice.actions;
export default customProductsSlice.reducer;
